// ── Adapter Supabase do IPipelinesConfigRepository ────────────────
// Única camada que conhece as tabelas funnels/pipelines/pipeline_stages para
// as telas de configuração (Settings).

import { getSupabase } from '@infra/supabase/client';
import type { Database } from '@/types/database.types';
import type {
  IPipelinesConfigRepository,
  FunnelRow,
  PipelineRow,
  StageRow,
  PipelineWithFunnel,
  PipelineWithStages,
  CloserPipeline,
  CreatePipelineParams,
  InsertStageParams,
} from '@modules/pipelinesConfig';

type PipelineRole = Database['public']['Tables']['pipelines']['Row']['role'];

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabasePipelinesConfigRepository implements IPipelinesConfigRepository {
  listFunnelsFull = async (workspaceId: string): Promise<FunnelRow[]> => {
    const { data } = await client()
      .from('funnels').select('*')
      .eq('workspace_id', workspaceId).eq('ativo', true).order('name');
    return (data || []) as unknown as FunnelRow[];
  };

  listActivePipelines = async (workspaceId: string): Promise<PipelineRow[]> => {
    const { data } = await client()
      .from('pipelines').select('*')
      .eq('workspace_id', workspaceId).eq('ativo', true).order('role');
    return (data || []) as unknown as PipelineRow[];
  };

  listPipelinesInFunnel = async (funnelId: string): Promise<PipelineRow[]> => {
    const { data } = await client()
      .from('pipelines').select('*')
      .eq('funnel_id', funnelId).eq('ativo', true).order('role');
    return (data || []) as unknown as PipelineRow[];
  };

  getStageNames = async (stageIds: string[]): Promise<Record<string, string>> => {
    if (!stageIds.length) return {};
    const { data } = await client().from('pipeline_stages').select('id, name').in('id', stageIds);
    const map: Record<string, string> = {};
    (data || []).forEach((s) => { map[s.id] = s.name; });
    return map;
  };

  listPipelinesForBoard = async (workspaceId: string, funnelId: string | null): Promise<PipelineWithFunnel[]> => {
    let q = client()
      .from('pipelines').select('*, funnel:funnels (id, name)')
      .eq('workspace_id', workspaceId).eq('ativo', true);
    if (funnelId) q = q.eq('funnel_id', funnelId);
    const { data, error } = await q.order('role');
    if (error) throw error;
    return (data || []) as unknown as PipelineWithFunnel[];
  };

  listStagesInPipeline = async (pipelineId: string): Promise<StageRow[]> => {
    const { data } = await client()
      .from('pipeline_stages').select('*')
      .eq('pipeline_id', pipelineId).eq('ativo', true).order('order_index');
    return (data || []) as unknown as StageRow[];
  };

  listInactiveStageIds = async (pipelineId: string): Promise<string[]> => {
    const { data, error } = await client()
      .from('pipeline_stages').select('id')
      .eq('pipeline_id', pipelineId).eq('ativo', false);
    if (error) throw error;
    return (data || []).map((r) => r.id);
  };

  listPipelinesWithStages = async (workspaceId: string): Promise<PipelineWithStages[]> => {
    const { data } = await client()
      .from('pipelines')
      .select('id, name, role, funnel_id, funnel:funnels ( id, name ), pipeline_stages ( id, name, order_index, ativo )')
      .eq('workspace_id', workspaceId).eq('ativo', true).order('role');
    return (data || []) as unknown as PipelineWithStages[];
  };

  listCloserPipelinesInWorkspace = async (workspaceId: string): Promise<CloserPipeline[]> => {
    const { data } = await client()
      .from('pipelines').select('id, name, funnel_id')
      .eq('workspace_id', workspaceId).eq('role', 'closer').eq('ativo', true).order('name');
    return (data || []) as unknown as CloserPipeline[];
  };

  updateFunnel = async (
    funnelId: string,
    updates: { name: string; description: string | null; lead_profile?: string },
  ): Promise<void> => {
    const { error } = await client().from('funnels').update(updates).eq('id', funnelId);
    if (error) throw error;
  };

  deactivateFunnel = async (funnelId: string): Promise<void> => {
    const { error } = await client().from('funnels').update({ ativo: false }).eq('id', funnelId);
    if (error) throw error;
  };

  setPipelineFunnel = async (pipelineId: string, funnelId: string | null): Promise<void> => {
    const { error } = await client().from('pipelines').update({ funnel_id: funnelId }).eq('id', pipelineId);
    if (error) throw error;
  };

  createPipeline = async ({ workspaceId, name, role, color }: CreatePipelineParams): Promise<PipelineRow | null> => {
    const { data, error } = await client()
      .from('pipelines')
      .insert({ workspace_id: workspaceId, name, role: role as PipelineRole, color, ativo: true })
      .select().single();
    if (error) throw error;
    return (data ?? null) as unknown as PipelineRow | null;
  };

  updatePipeline = async (pipelineId: string, updates: { name: string; color: string }): Promise<void> => {
    const { error } = await client().from('pipelines').update(updates).eq('id', pipelineId);
    if (error) throw error;
  };

  deactivatePipeline = async (pipelineId: string): Promise<void> => {
    const { error } = await client().from('pipelines').update({ ativo: false }).eq('id', pipelineId);
    if (error) throw error;
  };

  insertStage = async ({ pipelineId, name, color, orderIndex }: InsertStageParams): Promise<StageRow | null> => {
    const { data, error } = await client()
      .from('pipeline_stages')
      .insert({ pipeline_id: pipelineId, name, color, order_index: orderIndex, ativo: true })
      .select().single();
    if (error) throw error;
    return (data ?? null) as unknown as StageRow | null;
  };

  updateStage = async (stageId: string, updates: { name: string; color: string }): Promise<void> => {
    const { error } = await client().from('pipeline_stages').update(updates).eq('id', stageId);
    if (error) throw error;
  };

  updateStageOrder = async (stageId: string, orderIndex: number): Promise<void> => {
    const { error } = await client().from('pipeline_stages').update({ order_index: orderIndex }).eq('id', stageId);
    if (error) throw error;
  };

  deactivateStage = async (stageId: string, orderIndex: number): Promise<void> => {
    const { error } = await client()
      .from('pipeline_stages').update({ ativo: false, order_index: orderIndex }).eq('id', stageId);
    if (error) throw error;
  };
}
